import { useAnimal } from '@/api/hooks';
import { AnimalCoverPhotoPicker } from '@/components/animals/AnimalCoverPhotoPicker';
import { AnimalTimeline } from '@/components/animals/AnimalTimeline';
import { Button } from '@/components/ui/Button';
import { FloatingWindow } from '@/components/ui/FloatingWindow';
import { formatDate, formatWeight, sexLabel, breedLabel } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useNavigate } from 'react-router-dom';

type AnimalFloatingViewProps = {
  animalId: number;
  lotName?: string | null;
  stackIndex: number;
  zIndex: number;
  onClose: () => void;
  onFocus: () => void;
};

export function AnimalFloatingView({
  animalId,
  lotName,
  stackIndex,
  zIndex,
  onClose,
  onFocus,
}: AnimalFloatingViewProps) {
  const t = useT();
  const navigate = useNavigate();
  const { data: animal, isLoading, isError } = useAnimal(animalId);

  function openFullPage() {
    navigate(`/animais/${animalId}`);
    onClose();
  }

  return (
    <FloatingWindow
      title={isLoading ? 'Carregando…' : (animal?.internal_number ?? 'Animal')}
      subtitle={
        animal
          ? `${animal.name ?? 'Sem apelido'} · ${sexLabel(animal.sex)}`
          : undefined
      }
      onClose={onClose}
      onFocus={onFocus}
      zIndex={zIndex}
      initialOffset={stackIndex}
      headerLeading={
        !isLoading && animal ? (
          <AnimalCoverPhotoPicker
            animalId={animalId}
            internalNumber={animal.internal_number}
            coverPhotoUrl={animal.cover_photo_url}
            sex={animal.sex}
            avatarSize="sm"
          />
        ) : undefined
      }
      headerActions={
        !isLoading && animal ? (
          <button
            type="button"
            onClick={openFullPage}
            className="rounded-lg px-2.5 py-1 text-xs font-semibold text-manejai-forest transition hover:bg-manejai-sand/70"
          >
            {t('animal.access')}
          </button>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          <div className="h-20 rounded-xl bg-manejai-sand/40 animate-pulse" />
          <div className="h-32 rounded-xl bg-manejai-sand/40 animate-pulse" />
        </div>
      ) : isError ? (
        <p className="text-sm text-manejai-bark/60">Não foi possível carregar o animal. Tente novamente.</p>
      ) : !animal ? (
        <p className="text-sm text-manejai-bark/60">Animal não encontrado.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-2 rounded-xl bg-manejai-cream/50 p-3 text-sm">
            <InfoRow label="Categoria" value={animal.category_name} />
            <InfoRow
              label="Raça"
              value={breedLabel({
                abbreviation: animal.breed_abbreviation,
                name: animal.breed_name,
              })}
            />
            <InfoRow label="Lote" value={lotName ?? (animal.lot_id ? '—' : 'Sem lote')} />
            <InfoRow label="Peso atual" value={formatWeight(animal.current_weight_kg)} />
            <InfoRow label="Nascimento" value={formatDate(animal.birth_date)} />
            <InfoRow label="Chip" value={animal.chip_number ?? '—'} />
            <InfoRow label="Registro" value={animal.registry_code ?? '—'} />
          </div>

          <AnimalTimeline animalId={animalId} embedded />

          <Button type="button" className="w-full" onClick={openFullPage}>
            {t('animal.access')}
          </Button>
        </div>
      )}
    </FloatingWindow>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-manejai-bark/60">{label}</span>
      <span className="font-medium text-manejai-forest text-right">{value}</span>
    </div>
  );
}
